import React from "react";
import { motion } from "framer-motion";
import { Bell, Clock, Search, RotateCcw, Lightbulb, FileEdit, Check, Clipboard, Backpack, Plus, Settings } from "lucide-react";
import { useTranslation } from "react-i18next";

const RemindersWidget: React.FC = () => {
    const { t } = useTranslation("nonHealth");

    return (
        <motion.div
            key="reminders-widget"
            className="relative w-full max-w-sm"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
        >
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-teal-100 relative overflow-hidden">
                {/* افکت‌های تزئینی پس‌زمینه */}
                <motion.div
                    className="absolute -top-10 -left-10 w-24 h-24 bg-gradient-to-br from-teal-200/40 to-cyan-300/40 rounded-full blur-xl"
                    animate={{
                        scale: [1, 1.2, 1],
                        opacity: [0.5, 0.7, 0.5],
                        x: [0, 5, 0],
                        y: [0, -5, 0],
                    }}
                    transition={{
                        duration: 5,
                        repeat: Infinity,
                        repeatType: "mirror",
                    }}
                />
                <motion.div
                    className="absolute -bottom-8 -right-8 w-20 h-20 bg-gradient-to-tr from-emerald-200/30 to-teal-300/30 rounded-full blur-xl"
                    animate={{
                        scale: [1, 1.3, 1],
                        opacity: [0.4, 0.6, 0.4],
                        x: [0, -5, 0],
                        y: [0, 5, 0],
                    }}
                    transition={{
                        duration: 4,
                        repeat: Infinity,
                        repeatType: "mirror",
                        delay: 1,
                    }}
                />

                {/* نوار پیشرفت زمان */}
                <motion.div
                    className="absolute top-0 left-0 h-1 bg-gradient-to-r from-teal-400 to-cyan-400"
                    initial={{ width: "0%" }}
                    animate={{ width: "100%" }}
                    transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
                />

                {/* بخش اصلی */}
                <div className="bg-gradient-to-br from-teal-50 to-cyan-100 p-4 rounded-xl mb-3 shadow-inner relative space-y-4">
                    <motion.div
                        className="absolute top-2 right-2 w-8 h-8 bg-teal-600 rounded-full flex items-center justify-center text-white shadow-md"
                        whileHover={{ scale: 1.1, rotate: -10 }}
                        whileTap={{ scale: 0.9 }}
                        animate={{
                            boxShadow: ["0 2px 5px rgba(20, 184, 166, 0.3)", "0 4px 10px rgba(20, 184, 166, 0.5)", "0 2px 5px rgba(20, 184, 166, 0.3)"],
                            rotate: [0, 0, 5, -5, 0],
                            scale: [1, 1, 1.1, 1, 1],
                        }}
                        transition={{
                            duration: 3,
                            repeat: Infinity,
                            times: [0, 0.7, 0.8, 0.9, 1],
                        }}
                    >
                        <Bell className="text-lg" />
                    </motion.div>

                    <h5 className="text-teal-800 font-bold text-lg mb-3 pr-10">
                        <motion.span initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2, duration: 0.5 }}>
                            {t("widgets.reminders.importantReminder")}
                        </motion.span>
                    </h5>

                    {/* کارت یادآوری */}
                    <motion.div
                        className="flex items-center mb-2 bg-white/60 p-2 rounded-lg shadow-sm relative overflow-hidden"
                        whileHover={{
                            y: -3,
                            boxShadow: "0 6px 12px rgba(20, 184, 166, 0.15)",
                            backgroundColor: "rgba(255, 255, 255, 0.8)",
                        }}
                        initial={{ x: 20, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ duration: 0.4, delay: 0.3 }}
                    >
                        <div className="absolute right-0 top-0 w-1 h-full bg-gradient-to-b from-red-400 to-rose-600" />
                        <motion.div
                            className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center ml-3 text-teal-700"
                            animate={{
                                y: [0, -3, 0],
                                boxShadow: ["0 2px 4px rgba(20, 184, 166, 0.2)", "0 4px 8px rgba(20, 184, 166, 0.3)", "0 2px 4px rgba(20, 184, 166, 0.2)"],
                            }}
                            transition={{
                                duration: 1.5,
                                repeat: Infinity,
                                repeatType: "loop",
                                delay: 0.5,
                            }}
                        >
                            <FileEdit className="text-xl" />
                        </motion.div>
                        <div className="flex-1">
                            <p className="text-teal-700 text-sm font-medium">{t("widgets.reminders.scienceHomework")}</p>
                            <motion.p
                                className="font-bold text-teal-900 flex items-center gap-1"
                                animate={{
                                    color: ["rgb(19, 78, 74)", "rgb(15, 118, 110)", "rgb(19, 78, 74)"],
                                }}
                                transition={{ duration: 2, repeat: Infinity }}
                            >
                                <motion.span
                                    className="inline-block"
                                    animate={{ scale: [1, 1.1, 1] }}
                                    transition={{
                                        duration: 1,
                                        repeat: Infinity,
                                        repeatType: "mirror",
                                    }}
                                >
                                    <Clock className="w-4 h-4" />
                                </motion.span>
                                {t("widgets.reminders.tomorrow9am")}
                            </motion.p>
                        </div>
                        <motion.div
                            className="w-6 h-6 bg-teal-50 rounded-full flex items-center justify-center ml-1"
                            whileHover={{
                                scale: 1.2,
                                backgroundColor: "rgb(204, 251, 241)",
                            }}
                            whileTap={{ scale: 0.9 }}
                        >
                            <Check className="w-3 h-3" />
                        </motion.div>
                    </motion.div>

                    {/* نوار اولویت */}
                    <motion.div
                        className="flex justify-between items-center mb-2 bg-white/40 rounded-lg p-2"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.4 }}
                    >
                        <div className="flex items-center gap-2">
                            <p className="text-teal-600 text-xs">{t("widgets.reminders.priority")}</p>
                            <motion.span
                                className="text-white font-bold bg-gradient-to-r from-red-500 to-rose-600 px-2 py-1 rounded-md text-xs"
                                animate={{
                                    scale: [1, 1.05, 1],
                                    boxShadow: ["0 2px 4px rgba(244, 63, 94, 0.3)", "0 4px 8px rgba(244, 63, 94, 0.4)", "0 2px 4px rgba(244, 63, 94, 0.3)"],
                                }}
                                transition={{ duration: 1.5, repeat: Infinity }}
                            >
                                {t("widgets.reminders.high")}
                            </motion.span>
                        </div>
                        <div className="flex items-center gap-1">
                            <span className="text-xs text-teal-700">{t("widgets.reminders.timeLeft")}</span>
                            <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                                <motion.div
                                    className="h-full bg-gradient-to-r from-red-500 to-rose-500"
                                    initial={{ width: "70%" }}
                                    animate={{ width: ["70%", "30%", "70%"] }}
                                    transition={{
                                        duration: 8,
                                        repeat: Infinity,
                                        repeatType: "mirror",
                                    }}
                                />
                            </div>
                        </div>
                    </motion.div>
                </div>

                {/* کارت‌های اطلاعاتی */}
                <div className="grid grid-cols-2 gap-3 mb-3 text-xs">
                    <motion.div
                        className="bg-teal-50 p-3 rounded-lg shadow-sm border border-teal-200 relative overflow-hidden"
                        whileHover={{
                            y: -3,
                            boxShadow: "0 6px 12px rgba(20, 184, 166, 0.15)",
                            backgroundColor: "rgb(240, 253, 250)",
                        }}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4, delay: 0.6 }}
                    >
                        <div className="text-teal-700 font-medium mb-1">{t("widgets.reminders.activeReminders")}</div>
                        <div className="flex items-center">
                            <motion.div
                                className="relative"
                                animate={{ scale: [1, 1.2, 1] }}
                                transition={{
                                    duration: 1.5,
                                    repeat: Infinity,
                                    repeatType: "mirror",
                                }}
                            >
                                <span className="font-bold text-teal-600 text-base ml-1">۳</span>
                                <motion.div
                                    className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"
                                    animate={{
                                        scale: [1, 1.5, 1],
                                        opacity: [0.7, 1, 0.7],
                                    }}
                                    transition={{ duration: 1, repeat: Infinity }}
                                />
                            </motion.div>
                            <span className="text-gray-600">{t("widgets.reminders.otherItems")}</span>
                        </div>
                        <motion.div
                            className="absolute -bottom-1 -right-1 w-10 h-10 flex items-center justify-center text-teal-500"
                            animate={{
                                rotate: [0, 10, -10, 0],
                                scale: [1, 1.1, 1],
                            }}
                            transition={{
                                duration: 3,
                                repeat: Infinity,
                                repeatType: "loop",
                            }}
                        >
                            <Clipboard className="w-5 h-5" />
                        </motion.div>
                        <motion.div
                            className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-teal-400 to-emerald-500"
                            initial={{ scaleX: 0 }}
                            whileHover={{ scaleX: 1 }}
                            transition={{ duration: 0.3 }}
                        />
                    </motion.div>

                    <motion.div
                        className="bg-cyan-50 p-3 rounded-lg shadow-sm border border-cyan-200 relative overflow-hidden"
                        whileHover={{
                            y: -3,
                            boxShadow: "0 6px 12px rgba(8, 145, 178, 0.15)",
                            backgroundColor: "rgb(236, 254, 255)",
                        }}
                        initial={{ opacity: 0, x: 10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4, delay: 0.7 }}
                    >
                        <div className="text-cyan-700 font-medium mb-1">{t("widgets.reminders.recentCategory")}</div>
                        <div className="flex items-center gap-1">
                            <motion.span className="inline-block text-sm" animate={{ y: [0, -2, 0] }} transition={{ duration: 1.5, repeat: Infinity }}>
                                <Backpack className="w-4 h-4" />
                            </motion.span>
                            <p className="text-gray-600 font-semibold">{t("widgets.reminders.schoolHomework")}</p>
                        </div>
                        <motion.div
                            className="absolute -bottom-1 -right-1 w-10 h-10 flex items-center justify-center text-cyan-500 opacity-50"
                            animate={{
                                scale: [1, 1.1, 1],
                                opacity: [0.5, 0.7, 0.5],
                            }}
                            transition={{ duration: 2, repeat: Infinity }}
                        >
                            <Search className="w-5 h-5" />
                        </motion.div>
                        <motion.div
                            className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-cyan-400 to-sky-500"
                            initial={{ scaleX: 0 }}
                            whileHover={{ scaleX: 1 }}
                            transition={{ duration: 0.3 }}
                        />
                    </motion.div>
                </div>

                {/* نوار اکشن */}
                <motion.div className="flex items-center justify-between gap-2 mb-3" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.8 }}>
                    <motion.button
                        className="flex-1 bg-gradient-to-r from-teal-500 to-teal-600 text-white text-xs py-2 rounded-lg flex items-center justify-center gap-1 relative overflow-hidden"
                        whileHover={{
                            scale: 1.03,
                            boxShadow: "0 4px 8px rgba(20, 184, 166, 0.2)",
                        }}
                        whileTap={{ scale: 0.97 }}
                    >
                        <motion.span
                            animate={{ rotate: [0, 0, 180, 180, 0] }}
                            transition={{
                                duration: 1.5,
                                repeat: Infinity,
                                repeatType: "loop",
                                times: [0, 0.4, 0.5, 0.9, 1],
                            }}
                            className="inline-block"
                        >
                            <Plus className="w-4 h-4" />
                        </motion.span>
                        <span>{t("widgets.reminders.addReminder")}</span>
                    </motion.button>
                    <motion.button
                        className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center text-gray-500"
                        whileHover={{
                            scale: 1.1,
                            backgroundColor: "rgb(240, 253, 250)",
                            color: "rgb(20, 184, 166)",
                        }}
                        whileTap={{ scale: 0.9 }}
                    >
                        <RotateCcw className="w-4 h-4" />
                    </motion.button>
                    <motion.button
                        className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center text-gray-500"
                        whileHover={{
                            scale: 1.1,
                            backgroundColor: "rgb(240, 253, 250)",
                            color: "rgb(20, 184, 166)",
                        }}
                        whileTap={{ scale: 0.9 }}
                    >
                        <Settings className="w-4 h-4" />
                    </motion.button>
                </motion.div>

                {/* پیام راهنما */}
                <motion.div
                    className="bg-gradient-to-r from-blue-50 to-sky-50 border border-blue-200 text-blue-700 text-xs p-2 rounded-lg flex items-center relative overflow-hidden"
                    whileHover={{
                        scale: 1.02,
                        backgroundColor: "rgb(240, 249, 255)",
                    }}
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.9 }}
                >
                    <motion.span
                        className="text-sm ml-2 bg-blue-100 w-6 h-6 rounded-full flex items-center justify-center shadow-sm"
                        animate={{
                            rotate: [0, -10, 10, 0],
                            boxShadow: ["0 1px 3px rgba(59, 130, 246, 0.2)", "0 3px 6px rgba(59, 130, 246, 0.3)", "0 1px 3px rgba(59, 130, 246, 0.2)"],
                        }}
                        transition={{ duration: 3, repeat: Infinity }}
                    >
                        <Lightbulb className="w-4 h-4" />
                    </motion.span>
                    <span className="relative z-10">{t("widgets.reminders.tip")}</span>
                    <motion.div
                        className="absolute -top-6 -right-6 w-12 h-12 bg-blue-200/30 rounded-full blur-md"
                        animate={{
                            scale: [1, 1.5, 1],
                            opacity: [0.3, 0.5, 0.3],
                        }}
                        transition={{ duration: 4, repeat: Infinity }}
                    />
                </motion.div>
            </div>
        </motion.div>
    );
};

export default RemindersWidget;
